import React from 'react';
import { Target, Heart, Award, TrendingUp, Users, Clock } from 'lucide-react';
import { Card } from '../components/ui/card';
import { MobileCarousel } from '../components/MobileCarousel';

export const About = () => {
  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative bg-[#383D3B] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1686100510109-d520e59bf0ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsYXclMjBvZmZpY2UlMjB3b3Jrc3BhY2V8ZW58MHx8fHwxNzc2Mjg0ODc5fDA&ixlib=rb-4.1.0&q=85" alt="Modern office space" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#383D3B]/80"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">About IP Protection Office</h1>
            <p className="text-xl md:text-2xl text-gray-300">
              Dedicated professionals committed to making intellectual property protection accessible to every business
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#383D3B] mb-8 text-center">How We Got Here</h2>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>IP Protection Office was founded on a simple observation: too many business owners were either ignoring brand protection because of perceived complexity or overpaying traditional firms for services that could be streamlined. We set out to change that.</p>
              <p>Our founding team brought together intellectual property attorneys, process engineers, and client experience specialists to build a service model that delivers the same legal rigor large corporations rely on, at a price point that makes sense for startups, small businesses, and growing enterprises alike.</p>
              <p>Today we serve clients in every state, across every industry, from solo creators protecting a single mark to multi-brand portfolios with ongoing monitoring needs. Our measure of success is straightforward: when your brand is secure and you feel confident about the process, we have done our job.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#383D3B] mb-4">The Values Behind Every Filing</h2>
            <p className="text-lg text-gray-600">These principles shape every interaction we have with our clients</p>
          </div>
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { icon: Target, t: "Precision", d: "Every application is drafted with exacting attention to USPTO requirements and classification standards." },
              { icon: Heart, t: "Empathy", d: "We remember that behind every filing is a real person who has invested time, money, and emotion into their brand." },
              { icon: Award, t: "Integrity", d: "If we spot a problem with your mark's registrability, we tell you upfront rather than collecting fees on a doomed filing." },
              { icon: TrendingUp, t: "Efficiency", d: "Proprietary workflows and clear communication keep turnaround times short and administrative headaches minimal." }
            ].map((v, i) => (
              <Card key={i} className="p-8 text-center hover:shadow-xl transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#e8edc7] text-[#99AA38] mb-6"><v.icon className="h-8 w-8" /></div>
                <h3 className="text-xl font-bold text-[#383D3B] mb-4">{v.t}</h3>
                <p className="text-gray-600">{v.d}</p>
              </Card>
            ))}
          </div>
          <MobileCarousel>
            {[
              { icon: Target, t: "Precision", d: "Every application is drafted with exacting attention to USPTO requirements and classification standards." },
              { icon: Heart, t: "Empathy", d: "We remember that behind every filing is a real person who has invested time, money, and emotion into their brand." },
              { icon: Award, t: "Integrity", d: "If we spot a problem with your mark's registrability, we tell you upfront rather than collecting fees on a doomed filing." },
              { icon: TrendingUp, t: "Efficiency", d: "Proprietary workflows and clear communication keep turnaround times short and administrative headaches minimal." }
            ].map((v, i) => (
              <Card key={i} className="p-8 text-center shadow-lg">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#e8edc7] text-[#99AA38] mb-6"><v.icon className="h-8 w-8" /></div>
                <h3 className="text-xl font-bold text-[#383D3B] mb-4">{v.t}</h3>
                <p className="text-gray-600">{v.d}</p>
              </Card>
            ))}
          </MobileCarousel>
        </div>
      </section>

      {/* Approach */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#383D3B] mb-8 text-center">Our Approach to Client Service</h2>
            <div className="space-y-8">
              {[
                { t: "Honest Assessments", d: "Before you spend a dollar on filing fees, we evaluate your mark and give you a candid opinion on its strengths, weaknesses, and likelihood of approval. That transparency saves time and money." },
                { t: "Deep Domain Knowledge", d: "Our attorneys focus exclusively on trademark and intellectual property law. That specialization means faster case resolution, fewer mistakes, and more informed strategic advice." },
                { t: "Technology-Backed Workflows", d: "We use secure digital systems to draft, review, and file applications. Real-time status dashboards give you visibility into every stage without needing to call or email for updates." },
                { t: "Long-Term Partnership", d: "Registration is a milestone, not a finish line. We offer ongoing monitoring, renewal reminders, and enforcement support to keep your mark protected for years to come." }
              ].map((a, i) => (
                <div key={i}>
                  <h3 className="text-2xl font-bold text-[#383D3B] mb-4">{a.t}</h3>
                  <p className="text-lg text-gray-700 leading-relaxed">{a.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="relative py-20 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1767342007060-694d6a7db73f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMGNpdHlzY2FwZSUyMHNreWxpbmUlMjBuaWdodCUyMGRhcmt8ZW58MHx8fHwxNzc2Mjg2OTQzfDA&ixlib=rb-4.1.0&q=85" alt="City skyline at night" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#383D3B]/85"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6">Our Mission</h2>
            <p className="text-xl leading-relaxed text-gray-300">
              To remove the barriers between great ideas and the legal protection they deserve. We exist to ensure that business owners of every size can confidently build, grow, and defend their brands without navigating the legal system alone.
            </p>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="w-full">
              <img src="https://images.unsplash.com/photo-1686100510242-2521b6f89a68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBpbnRlcmlvciUyMGRlc2lnbnxlbnwwfHx8fDE3NzYyODQ4OTZ8MA&ixlib=rb-4.1.0&q=85" alt="Modern corporate interior" className="w-full h-64 lg:h-auto object-cover rounded-2xl shadow-2xl" />
            </div>
            <div className="w-full">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#383D3B] mb-6">What Makes Us Different</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">Among dozens of online trademark services, IP Protection Office stands apart through a combination of genuine legal expertise, responsive communication, and pricing that respects your budget.</p>
              <div className="space-y-6">
                {[
                  { icon: Award, t: "Focused Expertise", d: "IP law is all we do. That singular focus means we stay current on every rule change and examiner trend." },
                  { icon: Users, t: "Dedicated Case Managers", d: "You will never be routed to a random agent. Your assigned specialist knows your case history and goals." },
                  { icon: Clock, t: "Streamlined Processes", d: "Digital intake, automated status updates, and direct attorney access mean less waiting and more progress." }
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#e8edc7] flex items-center justify-center"><item.icon className="h-6 w-6 text-[#99AA38]" /></div>
                    <div>
                      <h3 className="text-xl font-bold text-[#383D3B] mb-2">{item.t}</h3>
                      <p className="text-gray-600">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Numbers */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#383D3B] mb-4">Results That Speak for Themselves</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Measurable outcomes across thousands of filings nationwide</p>
          </div>
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { v: "10,000+", l: "Applications Filed", d: "Across every trademark class and industry" },
              { v: "98%", l: "Approval Rate", d: "Consistently above the national average" },
              { v: "15+", l: "Years in Practice", d: "Deep, accumulated expertise in IP law" },
              { v: "24/7", l: "Support Access", d: "Help when you need it, not just during office hours" }
            ].map((s, i) => (
              <Card key={i} className="p-8 text-center hover:shadow-xl transition-shadow">
                <div className="text-5xl font-bold text-[#99AA38] mb-2">{s.v}</div>
                <div className="text-[#383D3B] font-semibold mb-2">{s.l}</div>
                <p className="text-gray-600 text-sm">{s.d}</p>
              </Card>
            ))}
          </div>
          <MobileCarousel autoPlayInterval={3500}>
            {[
              { v: "10,000+", l: "Applications Filed", d: "Across every trademark class and industry" },
              { v: "98%", l: "Approval Rate", d: "Consistently above the national average" },
              { v: "15+", l: "Years in Practice", d: "Deep, accumulated expertise in IP law" },
              { v: "24/7", l: "Support Access", d: "Help when you need it, not just during office hours" }
            ].map((s, i) => (
              <Card key={i} className="p-8 text-center shadow-lg">
                <div className="text-5xl font-bold text-[#99AA38] mb-2">{s.v}</div>
                <div className="text-[#383D3B] font-semibold mb-2">{s.l}</div>
                <p className="text-gray-600 text-sm">{s.d}</p>
              </Card>
            ))}
          </MobileCarousel>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto">
            <div className="w-full lg:order-2">
              <img src="https://images.unsplash.com/photo-1758519288417-d359ac3c494d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxsZWdhbCUyMHRlYW0lMjBjb2xsYWJvcmF0aW9uJTIwcHJvZmVzc2lvbmFsc3xlbnwwfHx8fDE3NzYyODQ4Nzl8MA&ixlib=rb-4.1.0&q=85" alt="Two professionals discussing over coffee" className="w-full h-64 lg:h-auto object-cover rounded-2xl shadow-2xl" />
            </div>
            <div className="w-full lg:order-1">
              <h2 className="text-3xl md:text-4xl font-bold text-[#383D3B] mb-6">A Collaborative Process from Start to Finish</h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">We keep you in the loop at every stage so there are never any surprises along the way.</p>
              <div className="space-y-6">
                {[
                  { t: "Discovery Call", d: "We learn about your brand, business model, and goals to build a tailored protection strategy." },
                  { t: "Research & Analysis", d: "Our team runs multi-source searches and delivers a written conflict report with clear next steps." },
                  { t: "Drafting & Filing", d: "Attorneys prepare your application with precise descriptions and file it electronically with the USPTO." },
                  { t: "Post-Filing Support", d: "We monitor your application, respond to office actions, and notify you of key milestones." }
                ].map((step, i) => (
                  <div key={i} className="border-l-4 border-[#99AA38] pl-6">
                    <h3 className="text-xl font-bold text-[#383D3B] mb-2">{step.t}</h3>
                    <p className="text-gray-600">{step.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="py-20 bg-[#f5f7eb]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#383D3B] mb-8 text-center">Commitments We Make to Every Client</h2>
            <div className="hidden md:grid md:grid-cols-2 gap-6">
              {[
                { t: "Plain-Language Updates", d: "We translate legal jargon into clear, actionable information so you always understand where things stand." },
                { t: "Realistic Expectations", d: "If a mark has registrability concerns, we surface them early and discuss alternatives before you invest further." },
                { t: "Fast Issue Resolution", d: "When the USPTO raises questions, our attorneys respond promptly with well-reasoned arguments to keep your file moving." },
                { t: "Growth-Ready Support", d: "As your business scales, we help you expand your trademark portfolio and adapt your protection strategy." }
              ].map((c, i) => (
                <Card key={i} className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="text-xl font-bold text-[#383D3B] mb-3">{c.t}</h3>
                  <p className="text-gray-600">{c.d}</p>
                </Card>
              ))}
            </div>
            <MobileCarousel>
              {[
                { t: "Plain-Language Updates", d: "We translate legal jargon into clear, actionable information so you always understand where things stand." },
                { t: "Realistic Expectations", d: "If a mark has registrability concerns, we surface them early and discuss alternatives before you invest further." },
                { t: "Fast Issue Resolution", d: "When the USPTO raises questions, our attorneys respond promptly with well-reasoned arguments to keep your file moving." },
                { t: "Growth-Ready Support", d: "As your business scales, we help you expand your trademark portfolio and adapt your protection strategy." }
              ].map((c, i) => (
                <Card key={i} className="p-6 shadow-lg">
                  <h3 className="text-xl font-bold text-[#383D3B] mb-3">{c.t}</h3>
                  <p className="text-gray-600">{c.d}</p>
                </Card>
              ))}
            </MobileCarousel>
          </div>
        </div>
      </section>
    </div>
  );
};
